import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Printer, Download, Share2 } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';
import { buildBudgetShareText, absoluteAppUrl } from '../utils/share';
import { ShareSheet } from '../components/ui/ShareSheet';

const CATEGORY_LABEL: Record<string, string> = {
  material: 'Material',
  labor: 'Mano de obra',
  other: 'Otro',
};

function ItemsTable({ items }: { items: { id: string; description: string; category?: string; quantity: number; unitPrice: number; total: number; notes?: string }[] }) {
  return (
    <table className="bdoc-table">
      <thead>
        <tr>
          <th style={{ width: '52%' }}>Descripción</th>
          <th className="ctr">Cant.</th>
          <th className="num">P. unit.</th>
          <th className="num">Importe</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={item.id}>
            <td>
              <span style={{ fontWeight: 600 }}>{item.description}</span>
              {item.category && <span className="bdoc-cat">{CATEGORY_LABEL[item.category] || item.category}</span>}
              {item.notes && <p className="bdoc-muted" style={{ margin: '4px 0 0', fontSize: 12 }}>{item.notes}</p>}
            </td>
            <td className="ctr">{formatNumber(item.quantity)}</td>
            <td className="num">{formatCurrency(item.unitPrice)}</td>
            <td className="num" style={{ fontWeight: 600 }}>{formatCurrency(item.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function BudgetDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { budgets, clients, company, isLoading } = useCompany();
  const [shareOpen, setShareOpen] = useState(false);
  const [ready, setReady] = useState(false);

  const budget = budgets.find(b => b.id === id);
  const client = budget ? clients.find(c => c.id === budget.clientId) : undefined;

  useEffect(() => {
    if (!budget) return;
    const t = window.setTimeout(() => setReady(true), 300);
    return () => window.clearTimeout(t);
  }, [budget]);

  useEffect(() => {
    if (ready && searchParams.get('print') === '1') {
      window.setTimeout(() => window.print(), 400);
    }
  }, [ready, searchParams]);

  if (isLoading) {
    return (
      <div className="bdoc-page">
        <div className="p-6 max-w-3xl mx-auto">
          <div className="b2b-card"><div className="animate-pulse h-64" /></div>
        </div>
      </div>
    );
  }

  if (!budget) {
    return (
      <div className="bdoc-page flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-[color:var(--b2b-danger)]" />
          <h1 className="text-2xl font-bold mb-4">Presupuesto no encontrado</h1>
          <Link to="/budgets" className="b2b-btn b2b-btn--primary">Ver presupuestos</Link>
        </div>
      </div>
    );
  }

  const statusConfig: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Borrador', cls: 'bdoc-badge--muted' },
    sent: { label: 'Enviado', cls: 'bdoc-badge--warn' },
    accepted: { label: 'Aceptado', cls: 'bdoc-badge--ok' },
    rejected: { label: 'Rechazado', cls: 'bdoc-badge--danger' },
    expired: { label: 'Expirado', cls: 'bdoc-badge--muted' },
  };
  const status = statusConfig[budget.status] || statusConfig.draft;
  const items = budget.items ?? [];
  const extraItems = budget.extraItems ?? [];
  const companyName = company?.name || 'Mi empresa';
  const shareUrl = absoluteAppUrl(`/budgets/${budget.id}`);
  const shareText = buildBudgetShareText({
    number: budget.number,
    companyName,
    clientName: client?.name,
    total: budget.total,
  });
  const paymentTerms = budget.paymentTerms || company?.paymentTerms;
  const warranty = budget.warrantyClauses || company?.warrantyClauses;

  return (
    <div className="bdoc-page">
      <div className="bdoc-toolbar no-print">
        <button type="button" className="b2b-btn b2b-btn--ghost" onClick={() => navigate(`/budgets/${budget.id}`)}>
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex-1" />
        <button type="button" className="b2b-btn b2b-btn--ghost" onClick={() => window.print()}>
          <Printer className="w-4 h-4" /> Imprimir
        </button>
        <button type="button" className="b2b-btn b2b-btn--ghost" onClick={() => window.print()}>
          <Download className="w-4 h-4" /> Descargar PDF
        </button>
        <button type="button" className="b2b-btn b2b-btn--primary" onClick={() => setShareOpen(true)}>
          <Share2 className="w-4 h-4" /> Compartir
        </button>
      </div>

      <div className="bdoc-sheet">
        <header className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            {company?.logo ? (
              <img src={company.logo} alt={companyName} className="bdoc-logo" />
            ) : (
              <div className="bdoc-logo bdoc-logo--fallback">{companyName.charAt(0)}</div>
            )}
            <div className="min-w-0">
              <p style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{companyName}</p>
              <div className="bdoc-muted" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
                {company?.fiscalName && company.fiscalName !== companyName && <div>{company.fiscalName}</div>}
                {company?.cif && <div>CIF: {company.cif}</div>}
                {company?.address && <div>{company.address}</div>}
                <div>
                  {[company?.phone, company?.email].filter(Boolean).join(' · ')}
                </div>
                {company?.website && <div>{company.website}</div>}
              </div>
            </div>
          </div>

          <div className="sm:text-right shrink-0">
            <p className="bdoc-title bdoc-accent">PRESUPUESTO</p>
            <p style={{ fontSize: 18, fontWeight: 700, marginTop: 6 }}>{budget.number}</p>
            <div className="mt-3 sm:flex sm:justify-end">
              <span className={['bdoc-badge', status.cls].join(' ')}>{status.label}</span>
            </div>
          </div>
        </header>

        <div className="bdoc-line" style={{ borderTopWidth: 1, borderTopStyle: 'solid', margin: '28px 0 24px' }} />

        <section className="grid gap-6 sm:grid-cols-2">
          <div className="bdoc-avoid">
            <p className="bdoc-section-label">Presupuesto para</p>
            <div className="mt-2 space-y-1">
              <p style={{ fontSize: 16, fontWeight: 700 }}>{client?.name || 'Cliente sin asignar'}</p>
              {client?.fiscalName && client.fiscalName !== client.name && (
                <p className="bdoc-muted" style={{ fontSize: 13 }}>{client.fiscalName}</p>
              )}
              {client?.cif && <p className="bdoc-muted" style={{ fontSize: 13 }}>CIF/NIF: {client.cif}</p>}
              {client?.address && <p className="bdoc-muted" style={{ fontSize: 13 }}>{client.address}</p>}
              {(client?.phone || client?.email) && (
                <p className="bdoc-muted" style={{ fontSize: 13 }}>
                  {[client?.phone, client?.email].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>

          <div className="bdoc-avoid sm:text-right">
            <p className="bdoc-section-label">Detalles</p>
            <div className="mt-2 space-y-1" style={{ fontSize: 13 }}>
              <div>
                <span className="bdoc-muted">Fecha: </span>
                <span style={{ fontWeight: 600 }}>{formatDate(budget.issueDate)}</span>
              </div>
              <div>
                <span className="bdoc-muted">Válido hasta: </span>
                <span style={{ fontWeight: 600 }}>{formatDate(budget.expiryDate)}</span>
              </div>
              {budget.estimatedStartDate && (
                <div>
                  <span className="bdoc-muted">Inicio estimado: </span>
                  <span style={{ fontWeight: 600 }}>{formatDate(budget.estimatedStartDate)}</span>
                </div>
              )}
              {budget.estimatedDays ? (
                <div>
                  <span className="bdoc-muted">Duración: </span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(budget.estimatedDays)} días</span>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="mt-8 bdoc-avoid">
          <p className="bdoc-section-label">Desglose de partidas</p>
          <div className="mt-3">
            {items.length > 0 ? (
              <ItemsTable items={items} />
            ) : (
              <p className="bdoc-muted" style={{ fontSize: 13 }}>Sin partidas.</p>
            )}
          </div>

          {extraItems.length > 0 && (
            <div className="mt-6">
              <p className="bdoc-section-label">Trabajos adicionales</p>
              <div className="mt-3">
                <ItemsTable items={extraItems} />
              </div>
            </div>
          )}
        </section>

        <section className="mt-6 flex justify-end bdoc-avoid">
          <div style={{ width: '100%', maxWidth: 340 }}>
            <div className="flex justify-between py-2" style={{ fontSize: 14 }}>
              <span className="bdoc-muted">Subtotal</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(budget.subtotal)}</span>
            </div>
            <div className="flex justify-between py-2" style={{ fontSize: 14 }}>
              <span className="bdoc-muted">IVA ({budget.vatRate}%)</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(budget.vatAmount)}</span>
            </div>
            <div className="bdoc-total-hero mt-3">
              <span className="label">Total</span>
              <span className="value">{formatCurrency(budget.total)}</span>
            </div>
          </div>
        </section>

        {(paymentTerms || warranty || budget.notes) && (
          <section className="mt-8 grid gap-5 sm:grid-cols-2 bdoc-avoid">
            {paymentTerms && (
              <div>
                <p className="bdoc-section-label">Condiciones de pago</p>
                <p className="bdoc-muted mt-2" style={{ fontSize: 12.5, whiteSpace: 'pre-line' }}>{paymentTerms}</p>
              </div>
            )}
            {warranty && (
              <div>
                <p className="bdoc-section-label">Garantía</p>
                <p className="bdoc-muted mt-2" style={{ fontSize: 12.5, whiteSpace: 'pre-line' }}>{warranty}</p>
              </div>
            )}
            {budget.notes && (
              <div className="sm:col-span-2">
                <p className="bdoc-section-label">Observaciones</p>
                <p className="bdoc-muted mt-2" style={{ fontSize: 12.5, whiteSpace: 'pre-line' }}>{budget.notes}</p>
              </div>
            )}
          </section>
        )}

        <section className="mt-10 grid gap-8 sm:grid-cols-2 bdoc-avoid">
          <div>
            <div className="bdoc-sign">
              {budget.clientSignature && <img src={budget.clientSignature} alt="Firma del cliente" />}
            </div>
            <p className="bdoc-muted mt-2" style={{ fontSize: 12 }}>
              Firma del cliente{budget.signedAt ? ` · ${formatDate(budget.signedAt)}` : ''}
            </p>
          </div>
          <div>
            <div className="bdoc-sign">
              {budget.companySignature && <img src={budget.companySignature} alt="Firma de la empresa" />}
            </div>
            <p className="bdoc-muted mt-2" style={{ fontSize: 12 }}>
              Firma de {companyName}
            </p>
          </div>
        </section>

        <footer className="bdoc-line mt-10 pt-4" style={{ borderTopWidth: 1, borderTopStyle: 'solid' }}>
          <p className="bdoc-muted" style={{ fontSize: 11.5, textAlign: 'center' }}>
            {[company?.fiscalName || companyName, company?.cif, company?.address].filter(Boolean).join(' · ')}
          </p>
          <p className="bdoc-muted" style={{ fontSize: 11.5, textAlign: 'center', marginTop: 2 }}>
            Documento generado con ReformAll · {formatDate(new Date())}
          </p>
        </footer>
      </div>

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`Presupuesto ${budget.number} · ${companyName}`}
        text={shareText}
        url={shareUrl}
      />
    </div>
  );
}
